import { flatten } from "lodash-es";

import { config } from "@/config";
import type { Dictionary, DictionaryMethod } from "@/types";

const { isMobile } = config.app;

export const ARABIC_DICTIONARY_IDS = ["ordbogen-daar"];
export const RUSSIAN_DICTIONARY_IDS = ["gyldendal-red-daru"];
export const NORDEA_TERMS_DICTIONARY_ID = "firm-32";
export const DANSKE_BANK_TERMS_DICTIONARY_ID = "firm-54";

function groupByPublisher(dictionaries: Dictionary[]): Record<string, Dictionary[]> {
  return Object.values(dictionaries).reduce<Record<string, Dictionary[]>>(
    (dictionariesByPublisher, dictionary) => ({
      ...dictionariesByPublisher,
      [dictionary.publisher]: {
        ...dictionariesByPublisher[dictionary.publisher],
        [dictionary.id]: dictionary
      }
    }),
    {}
  );
}

export function sortByPublisherPriority(dictionaries: Dictionary[]): Dictionary[] {
  const priorityMap: Record<string, number> = {
    "Ordbogen.com": 100,
    "Lemma.com": 100,
    Gyldendal: 200,
    "K Dictionaries": 1000
  };
  const defaultPriority = 300;

  const dictionariesByPublisher = groupByPublisher(dictionaries);

  return (
    Object.entries(dictionariesByPublisher)
      // Sort publisher subgroups using priority map
      .sort(([publisherA], [publisherB]) => {
        const priorityA = priorityMap[publisherA] ?? defaultPriority;
        const priorityB = priorityMap[publisherB] ?? defaultPriority;

        return priorityA - priorityB + publisherA.localeCompare(publisherB);
      })
      // Sort dictionaries alphabetically within each publisher subgroup
      .reduce<Dictionary[]>((sortedDictionaries, [, publisherDictionaryMap]) => {
        const sortedPublisherDictionaries = Object.values(publisherDictionaryMap).sort((dictionaryA, dictionaryB) =>
          dictionaryA.title.localeCompare(dictionaryB.title)
        );

        return [...sortedDictionaries, ...sortedPublisherDictionaries];
      }, [])
  );
}

export function getMethods(dictionaries: Dictionary[]): DictionaryMethod[] {
  return flatten(dictionaries.map((dictionary) => dictionary.methods));
}

export function isBiDirectional(dictionary: Dictionary): boolean {
  return dictionary.tags.includes("bidirect");
}

export function isCompanyDictionary(dictionary: Dictionary): boolean {
  return dictionary.tags.includes("company");
}

export function isCompanyDictionaryWithMethods(dictionary: Dictionary): boolean {
  return dictionary.tags.includes("company-with-methods");
}

export function hasAboutDictionary(dictionary: Dictionary): boolean {
  if (isMobile) {
    return false;
  }

  return dictionary.hasAbout;
}
